import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import cmsGridBlockColumnObject from '@/entities/cmsGridBlockColumn/interface'

import {
  ALL_GRID_BLOCKS_QUERY,
  SINGLE_GRID_BLOCK_QUERY,
  CREATE_GRID_BLOCK_MUTATION,
  UPDATE_GRID_BLOCK_MUTATION,
  DELETE_GRID_BLOCK_MUTATION,
  FORCE_DELETE_GRID_BLOCK_MUTATION,
  ADD_GRID_BLOCK_COLUMNS_MUTATION,
  REMOVE_GRID_BLOCK_COLUMNS_MUTATION,
  CREATE_GRID_BLOCK_COLUMNS_MUTATION,
  DELETE_GRID_BLOCK_COLUMNS_MUTATION,
  UPDATE_GRID_BLOCK_COLUMNS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsGridBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_GRID_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.gridBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_GRID_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.gridBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_GRID_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.gridBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_GRID_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.gridBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_GRID_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.gridBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_GRID_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.gridBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_GRID_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsGridBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_GRID_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsGridBlock
    })
  },

  assignColumns: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        columnIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_GRID_BLOCK_COLUMNS_MUTATION, variables)
  },

  unassignColumns: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        columnIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_GRID_BLOCK_COLUMNS_MUTATION, variables)
  },

  createColumns: async (id: string, items: Array<cmsGridBlockColumnObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        columns: items,
      },
    }
    return graphQLClient.request(CREATE_GRID_BLOCK_COLUMNS_MUTATION, variables)
  },

  deleteColumns: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        columnIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_GRID_BLOCK_COLUMNS_MUTATION, variables)
  },

  updateColumnsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        columnIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_GRID_BLOCK_COLUMNS_ORDER_MUTATION, variables)
  },
}

export default cmsGridBlockRepository
